import { useCallback, useMemo, useState } from 'react';
import { useCartContext } from '@srcDS/provider/cart/CartContext';
import { useNostoContext } from '@srcDS/provider/nosto/NostoContext';
import { SIZES_SORT_ORDER } from '../../organisms/productActions/utils/sortOrder';

export interface SizeButtonsProps {
  isAvailable: boolean;
  isLoading: boolean;
  isActionDisabled: boolean;
  key: string;
  label: string;
  onClick: () => void;
}

export const useQuickAdd = ({ product, variants = [], nostoPlacement, nostoSearchClickData, callbackFn }) => {
  const { addToCart: add } = useCartContext();
  const { nostoUpdateCart, nostoAddToCart, nostoRecordSearchAddToCart } = useNostoContext();
  const [loading, setLoading] = useState(null);

  const addToCart = useCallback(
    async (e, variant) => {
      e.stopPropagation();
      setLoading(variant.sku);

      try {
        const { type, hit } = nostoSearchClickData;
        const response = await add(variant, 1);

        nostoUpdateCart(response);
        nostoAddToCart?.({ productId: product.productId, nostoPlacement });

        nostoRecordSearchAddToCart({ type, hit });
        // TagManager.addToCart(product, variant, 1, response.cart);

        callbackFn?.();
      } catch (error) {
        console.error('Error adding to cart:', error);
      } finally {
        setLoading(null);
      }
    },
    [add, nostoAddToCart, nostoPlacement, nostoUpdateCart, product],
  );

  const sortedSizes = useMemo(() => {
    if (!variants) return [];
    return variants
      .map((variant) => ({
        ...variant,
        label: variant.attributes.size?.label,
      }))
      .sort((a, b) => SIZES_SORT_ORDER.indexOf(a.label) - SIZES_SORT_ORDER.indexOf(b.label));
  }, [variants]);

  const sizeButtons = useMemo(() => {
    return sortedSizes.map((variant, index) => {
      const isAvailable =
        variant.projectSpecificData?.availableQuantity > 0 ||
        (variant.isOnStock && variant.availability === 'InStock') ||
        (variant.isOnStock && variant.available);

      return {
        key: `${variant.sku}_${index}`,
        label: variant.label,
        isAvailable,
        isLoading: loading === variant.sku,
        isActionDisabled: loading,
        onClick: (event) => addToCart(event, variant),
      };
    });
  }, [sortedSizes, addToCart, loading]);

  return { sizeButtons, loading };
};
